<template>
  <div class="fill-height">
    <scatter-chart
      v-if="viewMode === 'chart'"
      class="fill-height"
      :series="getSeries()"
      :xaxis="{ title: { text: $t('gps_variables.volume') }}"
      :yaxis="{ title: { text: $t('gps_variables.intensity') }}"
      :annotations="getAnnotations()"
    >
      <template v-slot:header>
        <v-card-title class="pa-6 d-flex justify-space-between">
          <v-row
            dense
            align="center"
          >
            <v-col class="text-no-wrap">
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t('card_title.volume_and_intensity') }}{{ aggregation ? ": " + $t(`variables.${aggregation}.long`) : '' }}
              </div>
            </v-col>
          </v-row>
          <v-btn
            v-if="removable"
            icon
            style="pointer-events: auto"
            @click="$emit('remove')"
          >
            <v-icon small>
              fal fa-times
            </v-icon>
          </v-btn>
          <visual-list-toggle
            v-else
            :value.sync="viewMode"
          />
        </v-card-title>
      </template>
    </scatter-chart>

    <v-card
      v-else-if="viewMode === 'table'"
      class="fill-height rounded-lg"
    >
      <v-card-title class="pa-6 d-flex justify-space-between">
        <div class="subtitle-2 grey--text text--grey-100">
          {{ $t('card_title.volume_and_intensity') }}{{ aggregation ? ": " + $t(`variables.${aggregation}.long`) : '' }}
        </div>
        <div class="d-flex align-center">
          <visual-list-toggle
            v-if="!removable"
            :value.sync="viewMode"
          />
        </div>
      </v-card-title>

      <chart-list-view-table
        :headers="tableHeaders"
        :items="players"
      >
        <template v-slot:item.last_name="{ item }">
          {{ item.last_name }}, {{ item.first_name }}
        </template>
        <template v-slot:item.volume="{ item }">
          {{ getPlayerValue(item, 'UXVOL') }}
        </template>
        <template v-slot:item.intensity="{ item }">
          {{ getPlayerValue(item, 'UXINTS') }}
        </template>
      </chart-list-view-table>
    </v-card>
  </div>
</template>

<script>
import ScatterChart from '@/components/charts/ScatterChart.vue'
import VisualListToggle from '@/components/buttons/VisualListToggle.vue'
import ChartListViewTable from '@/components/charts/implementation/ChartListViewTable.vue'

export default {
  name: 'VolumeIntensityScatterChart',
  components: { ChartListViewTable, ScatterChart, VisualListToggle },
  props: {
    players: {
      type: Array,
      required: true
    },
    removable: {
      type: Boolean,
      default: false
    },
    aggregation: {
      type: String,
      default: null
    }
  },
  data: () => ({
    viewMode: 'chart'
  }),
  computed: {
    tableHeaders() {
      return [
        {
          text: this.$t('table_headers.name'),
          value: 'last_name',
          sortable: true,
          width: '40%'
        },
        {
          text: this.$t('gps_variables.volume'),
          value: 'volume',
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('gps_variables.intensity'),
          value: 'intensity',
          sortable: true,
          align: 'center'
        }
      ]
    }
  },
  methods: {
    getSeries() {
      return this.players.map(p => {
        const stats = this.aggregation && !this.removable ? p.latest_stats : p.player_stats
        const volKey = this.getKey('UXVOL')
        const intsKey = this.getKey('UXINTS')

        return {
          name: p.first_name[0] + '. ' + p.last_name,
          data: [
            [stats?.[volKey] || 0, stats?.[intsKey] || 0]
          ]
        }
      })
    },
    getAnnotations() {
      const stats = this.aggregation && !this.removable ? 'latest_stats' : 'player_stats'
      const volKey = this.getKey('UXVOL')
      const intsKey = this.getKey('UXINTS')

      const maxVol = Math.max(...this.players.map(p => Math.abs(p[stats]?.[volKey]) || 0)) / 2
      const maxInts = Math.max(...this.players.map(p => Math.abs(p[stats]?.[intsKey]) || 0)) / 2

      return {
        xaxis: [{
          x: maxVol,
          strokeDashArray: 0,
          borderColor: '#008FFB'
        }],
        yaxis: [{
          y: maxInts,
          strokeDashArray: 0,
          borderColor: '#008FFB'
        }]
      }
    },
    getKey(baseKey) {
      return this.aggregation && !this.removable ? `${baseKey}_${this.aggregation}` : baseKey
    },
    getPlayerValue(player, baseKey) {
      const stats = this.aggregation && !this.removable ? player.latest_stats : player.player_stats
      const key = this.getKey(baseKey)
      return stats?.[key] || 0
    }
  }
}
</script>
